import { useEffect, useRef, HTMLProps } from 'react';

export const IndeterminateCheckbox = ({
	/* 
		Checkbox inputs can only have two states: checked or unchecked. They can have any value, but they either submit that value (checked) or don’t (unchecked) with a form submission. The default is unchecked.
		However visually, there are actually three states a checkbox can be in: checked, unchecked, or indeterminate.
		Each checkbox may have child checkboxes. If all those children are checked, it may be checked. If none are checked, it is unchecked. If some of them are checked, then it’s in an indeterminate state (in this case symbolically meaning “partially” checked).
		You can’t make a checkbox indeterminate through HTML. There is no indeterminate attribute. It is a property of checkboxes though, which you can change via JavaScript.

		https://css-tricks.com/indeterminate-checkboxes/
	*/

	indeterminate,
	className = '',
	variant = 'sm',
	showEdgeSpacing = false, // when true adds a spacing element around the checkbox so that missed clicks don't cause click propagation. Used for table row checkboxes where the row is clickable.
	dataTestId,
	...rest
}: {
	indeterminate?: boolean;
	variant?: 'sm' | 'lg';
	showEdgeSpacing?: boolean;
	dataTestId?: string;
} & HTMLProps<HTMLInputElement>) => {
	const ref = useRef<HTMLInputElement>(null!);

	useEffect(() => {
		if (typeof indeterminate === 'boolean') {
			ref.current.indeterminate = !rest.checked && indeterminate;
		}
	}, [ref, indeterminate, rest.checked]);

	const sizeClass = variant === 'lg' ? 'w-5 h-5 rounded-sm outline-transparent outline-5' : '';

	return (
		<div className='relative flex-grow'>
			{showEdgeSpacing && (
				<span
					className='absolute -left-1 right-1 -top-1 -bottom-1 xl:-left-2 xl:right-0 xl:-top-2 xl:-bottom-2 z-0 cursor-auto'
					onClick={(e) => e.stopPropagation()}
					aria-hidden='true'
				></span>
			)}
			<input
				type='checkbox'
				data-test-id={dataTestId || 'tableCheckBox-select'}
				ref={ref}
				className={
					className + ' cursor-pointer text-primaryBrand z-1 relative ' + sizeClass
				}
				{...rest}
			/>
		</div>
	);
};
